
import Vue, { computed, onMounted, ref, watch } from 'vue'
import VueClipboard from 'vue-clipboard2'
import { defineComponent, PropType } from 'vue'

import AddToSharedFolder from './AddToSharedFolder.vue'

import { TechpackApi } from '../services/techpack.service'
import { Techpack, TechpackShare, TechpackEmail, Product, ProductTechpackSelection } from '../types'
import { createNamespacedHelpers } from 'vuex-composition-helpers'

const { useGetters: useUserGetters } = createNamespacedHelpers('user')
const { useActions: useSnackActions } = createNamespacedHelpers('snack')

VueClipboard.config.autoSetContainer = true
Vue.use(VueClipboard)

export default defineComponent({
  name: 'EmailTechpacks',
  components: { AddToSharedFolder },
  props: {
    selectedProducts: {
      type: Array as PropType<Array<Product>>,
      default: () => []
    }
  },
  setup(props, { emit }) {
    const { user } = useUserGetters(['user'])
    const { setSnack } = useSnackActions(['setSnack'])

    const step = ref(1)
    const productTechpackSelections = ref<Array<ProductTechpackSelection>>([])
    const shares = ref<Array<TechpackShare>>([])
    const email = ref<TechpackEmail>({
      content: { email_body: '', report_urls: [] },
      email_subject: '',
      receiver_emails: []
    })

    const selectedTechpacks = computed(() => {
      return productTechpackSelections.value
        .filter(selection => selection.selectedTechpack)
        .map(selection => selection.selectedTechpack) as Array<Techpack>
    })

    watch(
      () => props.selectedProducts,
      () => resetView()
    )

    onMounted(() => {
      resetView()
    })

    function resetView() {
      productTechpackSelections.value = []
      Promise.all(
        props.selectedProducts.map(async (product: Product) => {
          const techpacks = await TechpackApi.getTechpacks(product._id)
          return {
            productId: product._id,
            productName: product.name,
            techpacks,
            selectedTechpack: techpacks[0]
          }
        })
      ).then(res => {
        productTechpackSelections.value = res as unknown as ProductTechpackSelection[]
      })
      step.value = 1
      shares.value = []
    }

    function emailTechpacksAndClose() {
      TechpackApi.emailTechpacks(email.value)
      shareTechpacksAndClose()
    }

    function shareTechpacksAndClose() {
      setSnack({ text: `Techpack successfully shared`, variant: 'success' })
      resetView()
      emit('close-email-techpacks', true)
    }

    function removeProduct(product: ProductTechpackSelection) {
      productTechpackSelections.value = productTechpackSelections.value.filter(
        foundProduct => foundProduct.productId != product.productId
      )
    }

    function initializeEmailText() {
      email.value.email_subject = `${shares.value.length} tech packs have been shared with you.`
      email.value.content.email_body = `${user.value.name} has shared ${shares.value.length} tech packs with you.`
      email.value.receiver_emails = []
    }

    async function moveToSharesStep() {
      shares.value = []
      for (const techpack of selectedTechpacks.value) {
        shares.value.push(await TechpackApi.createTechpackShares(techpack.product._id, techpack._id))
      }
      step.value = 2
    }

    function moveToEmailsStep() {
      initializeEmailText()
      email.value.content.report_urls.push(
        ...shares.value.map((share: TechpackShare) => {
          const name = selectedTechpacks.value.find(techpack => techpack._id === share.productTechpack)?.name
          return { url: share.url, name: name ?? '' }
        })
      )
      step.value = 3
    }

    function stepBack() {
      step.value = 1
    }

    return {
      step,
      user,
      email,
      shares,
      stepBack,
      removeProduct,
      moveToSharesStep,
      moveToEmailsStep,
      selectedTechpacks,
      shareTechpacksAndClose,
      emailTechpacksAndClose,
      productTechpackSelections
    }
  }
})
